import { Component, Input } from '@angular/core';

import { ParticipantMarketType, ParticipantOption, ParticipantPriceType } from '@cds/betting-offer';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { TimeformParticipantWithEventOption, TimeformParticipantWithParticipantOption, WinGameFixture } from '@frontend/sports/racing/feature/models';
import { NOT_APPLICABLE, trackingConstants } from '@frontend/sports/tracking/feature';
import { IntlService } from '@frontend/vanilla/core';

import { ParticipantPickType } from '../../../../betslip/core/picks/pick-models';
import { BestOddsGuaranteedService } from '../../../best-odds-guarantee/services/best-odds-guaranteed.service';
import { HorsesFixtureParticipantHelper } from '../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureSortingHelper } from '../../../horse-racing-shared/services/horses-fixture-sorting-helper.service';

interface RacePreviewViewModel {
    race: Nullable<WinGameFixture>;
    runners: (TimeformParticipantWithParticipantOption | TimeformParticipantWithEventOption)[];
}

@Component({
    selector: 'ms-race-preview',
    templateUrl: 'race-preview.component.html',
    standalone: false,
})
export class RacePreviewComponent {
    @Input() trackingPosition: number;

    @Input() set race(value: WinGameFixture) {
        this.setViewModel(value);
    }

    vm: RacePreviewViewModel = {
        race: null,
        runners: [],
    };

    trackingOptions: { [key: string]: string };
    participantOptionGroups: { [id: string]: EventOption[] } = {};
    optionGroups: EventOptionGroup[];
    isOptionBet: boolean;
    participantPickType = ParticipantPickType;

    constructor(
        public siteCore: Sitecore,
        protected horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected horsesFixtureSortingHelper: HorsesFixtureSortingHelper,
        protected participantHelper: HorsesFixtureParticipantHelper,
        protected intlService: IntlService,
        protected bogService: BestOddsGuaranteedService,
    ) {}

    racePreviewTrackBy(index: number, runner: TimeformParticipantWithParticipantOption | TimeformParticipantWithEventOption): number {
        return runner.id;
    }

    isEventOption(option: EventOption | ParticipantOption | undefined): option is EventOption {
        return option instanceof EventOption;
    }

    isParticipantOption(option: EventOption | ParticipantOption | undefined): option is ParticipantOption {
        return !!option && 'fixtureParticipantId' in option;
    }

    protected setViewModel(fixture: WinGameFixture): void {
        if (fixture.participants.length === 0) {
            return;
        }
        const runners = this.participantHelper.getParticipants(fixture.participants);
        const options = this.horsesFixturePricingHelper.getPrices(fixture, ParticipantMarketType.Win, ParticipantPriceType.Fixed);
        const sortedParticipants = this.horsesFixtureSortingHelper.sortParticipantsByWinMarket(runners, options);
        const topThreeRunners = sortedParticipants.slice(0, 3);
        this.setTrackingOptions(fixture, this.bogService.isFixtureBogEligible(fixture));
        this.vm = {
            race: fixture,
            runners: topThreeRunners.map((r) => {
                const option = options[r.id];

                return {
                    ...r,
                    option,
                    priceHistory: this.horsesFixturePricingHelper.getPriceHistory(option),
                    attributes: this.participantHelper.getHorseRaceInfo(r),
                    isOptionActive: this.horsesFixturePricingHelper.isAvailableWinOption(option),
                };
            }),
        };
    }

    protected setTrackingOptions(fixture: WinGameFixture, isBestOddsEligible?: boolean) {
        this.trackingOptions = {
            [trackingConstants.COMPONENT_MODULE_NAME]: 'NextRaces',
            [trackingConstants.COMPONENT_MODULE_POSITION]: this.intlService.formatDate(fixture.startDate, 'HH:mm') || '',
            [trackingConstants.COMPONENT_CONTENT_POSITION]: this.trackingPosition?.toString(),
            [trackingConstants.COMPONENT_MODULE_CUSTOM_NAME]: fixture.name?.value,
            [trackingConstants.BET_DETAILS]: isBestOddsEligible ? 'BOG' : NOT_APPLICABLE,
        };
    }
}
